import React, { useMemo, useState } from "react";
import {
  Container,
  PageHeader,
  Card,
  Select,
  Input,
  SearchIcon,
  CountryLabel,
} from "../components/ui";
import { BuildingIcon, PinIcon } from "../components/ui/icons";
import { useClubs } from "../apiCalls/clubApiCalls";
import { getCountryName } from "../utils";
import Loader from "../components/Loader";
import ErrorState from "../components/ErrorState";
import EmptyState from "../components/EmptyState";

const Clubs = () => {
  const [country, setCountry] = useState("");
  const [search, setSearch] = useState("");
  const { data, isLoading, isError } = useClubs();

  const clubs = useMemo(() => data?.data ?? [], [data]);

  // Country options come from the clubs actually in the register rather than
  // the full ISO list, so the filter never offers an empty result.
  const countries = useMemo(() => {
    const codes = [...new Set(clubs.map((c) => c.country).filter(Boolean))];
    return codes
      .map((code) => ({ code, name: getCountryName(code) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [clubs]);

  const visible = clubs.filter((club) => {
    const matchesCountry = !country || club.country === country;
    const matchesSearch =
      !search ||
      club.name?.toLowerCase().includes(search.toLowerCase()) ||
      club.city?.toLowerCase().includes(search.toLowerCase());
    return matchesCountry && matchesSearch;
  });

  return (
    <>
      <PageHeader
        title="Clubs"
        subtitle="The clubs and venues that make up the PadelRank network, from local courts to international tournament hosts."
      />

      <Container className="pb-16 md:pb-20">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row">
          <div className="sm:max-w-xs sm:flex-1">
            <Input
              icon={<SearchIcon />}
              placeholder="Search clubs…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search clubs"
            />
          </div>
          <div className="sm:w-56">
            <Select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              aria-label="Filter by country"
            >
              <option value="">All Countries</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {isLoading && <Loader label="Loading clubs…" />}
        {isError && <ErrorState message="We couldn't load the clubs." />}
        {!isLoading && !isError && visible.length === 0 && (
          <EmptyState icon="🏟️" message="No clubs match your filters." />
        )}

        {!isLoading && !isError && visible.length > 0 && (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((club) => (
              <Card key={club._id} hoverable className="flex gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-sm border border-border bg-bg">
                  {club.logoUrl ? (
                    <img
                      src={club.logoUrl}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <BuildingIcon className="text-muted" />
                  )}
                </div>
                <div className="min-w-0">
                  <h2 className="truncate font-semibold text-navy">
                    {club.name}
                  </h2>
                  <div className="mt-1.5 flex items-center gap-1.5 text-sm text-muted">
                    <PinIcon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{club.city || "—"}</span>
                  </div>
                  <div className="mt-2">
                    <CountryLabel code={club.country} className="text-muted" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Container>
    </>
  );
};

export default Clubs;
